import { useEffect, useState } from "react";
import {
  disableAdminPush,
  enableAdminPush,
  getPushState,
  PUSH_STATE_CHANGED_EVENT,
  type PushState,
} from "@/lib/push";

function labelFor(state: PushState, busy: boolean): string {
  if (busy) return "Push...";
  if (state === "enabled") return "Push aan";
  if (state === "denied") return "Push geblokkeerd";
  if (state === "unsupported") return "Geen push";
  return "Push uit";
}

function titleFor(state: PushState): string {
  if (state === "enabled") return "Pushmeldingen voor nieuwe bestellingen en betaalstatussen staan aan.";
  if (state === "denied") return "Pushmeldingen zijn geblokkeerd in je browserinstellingen.";
  if (state === "unsupported") return "Deze browser of context ondersteunt geen PWA-push.";
  return "Zet pushmeldingen aan voor nieuwe bestellingen en betaalstatuswijzigingen.";
}

export function PushToggle() {
  const [state, setState] = useState<PushState>("default");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => {
      getPushState().then(setState).catch(() => setState("unsupported"));
    };
    refresh();
    window.addEventListener(PUSH_STATE_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(PUSH_STATE_CHANGED_EVENT, refresh);
  }, []);

  async function toggle() {
    if (busy || state === "unsupported" || state === "denied") return;
    setBusy(true);
    setError(null);
    try {
      const next = state === "enabled"
        ? await disableAdminPush()
        : await enableAdminPush();
      setState(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Push kon niet worden aangepast");
      setState(await getPushState().catch((): PushState => "unsupported"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="push-toggle-wrap">
      <button
        type="button"
        className={`push-toggle ${state === "enabled" ? "on" : ""}`}
        onClick={toggle}
        disabled={busy || state === "unsupported" || state === "denied"}
        title={titleFor(state)}
      >
        <span className="dot" />
        {labelFor(state, busy)}
      </button>
      {error && <div className="push-toggle-error">{error}</div>}
    </div>
  );
}
